import * as React from "react";
import clsx from "clsx";
import nouislider from "nouislider";
import { AnalyticsTrigger } from "../../helpers/analytics/analytics-trigger/analytics-trigger.mjs";
import { FormHelper } from "../form-helper/form-helper.mjs";
import debounce from "debounce";
import "tabbable";
import { Icon } from "../../icon/icon/icon.mjs";
import { useLabels } from "../../helpers/label-provider/use-labels.mjs";
const Slider = (props) => {
  const labels = useLabels();
  return /* @__PURE__ */ React.createElement(
    AnalyticsTrigger,
    {
      trigger: {
        callback: "onChange",
        id: "Slider - ".concat(props.id),
        elementId: props.id,
        elementType: "slider",
        interactionType: "select"
      }
    },
    /* @__PURE__ */ React.createElement(SliderInner, { ...props, labels })
  );
};
class SliderInner extends React.Component {
  constructor(props) {
    super(props);
    this.sliderContainer = null;
    this.lowerHandle = null;
    this.upperHandle = null;
    this.lowerTooltip = null;
    this.upperTooltip = null;
    this.collapsedTooltip = null;
    this.sliderConnect = null;
    this.origin = [];
    this.slider = null;
    this.tooltipStyleInterval = 0;
    this.tooltipStyleTimeout = 0;
    this.onDrag = () => {
      if (this.lowerHandle && this.lowerHandle.classList.contains("slider__active")) {
        this.handleTooltip(this.lowerHandle, this.lowerTooltip);
      }
      if (this.upperHandle && this.upperHandle.classList.contains("slider__active")) {
        this.handleTooltip(this.upperHandle, this.upperTooltip);
      }
      this.handleChange();
    };
    this.checkTooltipCollision = () => {
      if (this.lowerTooltip && this.upperTooltip) {
        const lowerTooltipRight = this.lowerTooltip.getBoundingClientRect().right;
        const upperTooltipLeft = this.upperTooltip.getBoundingClientRect().left;
        const padding = 5;
        if (lowerTooltipRight + padding >= upperTooltipLeft) {
          this.lowerTooltip.classList.add("slider__tooltip--hidden");
          this.upperTooltip.classList.add("slider__tooltip--hidden");
          this.collapseTooltips();
        } else {
          this.lowerTooltip.classList.remove("slider__tooltip--hidden");
          this.upperTooltip.classList.remove("slider__tooltip--hidden");
          this.unCollapseTooltips();
        }
      }
      if (this.state.tooltipsCollapsed) {
        this.setCollapsedTooltipPosition();
      }
    };
    this.handleChange = () => {
      this.handleTooltip(this.lowerHandle, this.lowerTooltip);
      this.handleTooltip(this.upperHandle, this.upperTooltip);
      if (!this.tooltipStyleInterval) {
        this.tooltipStyleInterval = window.setInterval(() => {
          this.handleChange();
        }, 1e3 / 60);
        window.clearTimeout(this.tooltipStyleTimeout);
        this.tooltipStyleTimeout = window.setTimeout(() => {
          window.clearInterval(this.tooltipStyleInterval);
          this.tooltipStyleInterval = 0;
        }, 300);
      }
      this.checkTooltipCollision();
    };
    this.handleTransitionEnd = (event) => {
      if (event.target && event.target instanceof Element && this.origin.includes(event.target) && event.propertyName === "transform") {
        window.clearTimeout(this.tooltipStyleTimeout);
        window.clearInterval(this.tooltipStyleInterval);
        this.tooltipStyleInterval = 0;
        this.handleChange();
      }
    };
    this.changeToOldValue = () => {
      if (this.slider && this.toNumber(this.slider.get()) !== this.state.value) {
        this.slider.set(this.state.value);
      }
    };
    this.renderPip = (pip, index) => {
      const total = this.props.range.max - this.props.range.min;
      let position = (pip.value - this.props.range.min) / total * 100;
      if (this.props.direction === "rtl") {
        position = 100 - position;
      }
      const isDisabled = Boolean(pip.disabled);
      const pipProps = isDisabled ? {
        "aria-hidden": true,
        tabIndex: -1
      } : {
        onClick: () => this.onPipClick(pip.value)
      };
      return /* @__PURE__ */ React.createElement("div", { key: index, className: this.pipBEM(pip), style: this.getPipStyle(pip.value, position), ...pipProps }, /* @__PURE__ */ React.createElement("div", { className: "slider__pip-inner" }, /* @__PURE__ */ React.createElement("div", { className: "slider__pip-label" }, pip.label ? pip.label : "".concat(pip.value).concat(this.props.unit)), pip.showAsterisk && /* @__PURE__ */ React.createElement(Icon, { className: "slider__pip-icon", modifiers: "#star" })));
    };
    this.state = {
      collapsedTooltipStyles: {},
      tooltipsCollapsed: false,
      value: this.props.start
    };
    this.debouncedResizeHandler = debounce(this.checkTooltipCollision.bind(this), 100);
  }
  static isStartEqual(start, prevStart) {
    if (typeof start === "number" && typeof prevStart === "number") {
      return start === prevStart;
    }
    if (Array.isArray(start) && Array.isArray(prevStart)) {
      return start[0] === prevStart[0] && start[1] === prevStart[1];
    }
    return false;
  }
  componentDidUpdate(prevProps) {
    this.handleSliderStartUpdate(prevProps);
    this.handleSliderOptionUpdates(prevProps);
  }
  handleSliderStartUpdate(prevProps) {
    if (this.slider && !SliderInner.isStartEqual(this.props.start, prevProps.start)) {
      this.slider.set(this.props.start);
      this.setState({ value: this.props.start });
    }
  }
  handleSliderOptionUpdates(prevProps) {
    if (this.slider) {
      const hasOnlyMinMax = Object.keys(this.props.range).length === 2 && "min" in this.props.range && "max" in this.props.range;
      const customRange = hasOnlyMinMax ? this.getFilteredRange() : this.props.range;
      const optionsToUpdate = [
        {
          condition: prevProps.range.min !== this.props.range.min || prevProps.range.max !== this.props.range.max || Object.keys(prevProps.range).length !== Object.keys(this.props.range).length,
          options: {
            range: customRange,
            start: this.props.start
          }
        },
        {
          condition: prevProps.unit !== this.props.unit,
          options: {
            format: this.setUnit(this.props.unit),
            range: customRange,
            start: this.props.start
          }
        },
        {
          condition: prevProps.step !== this.props.step,
          options: {
            range: customRange,
            start: this.props.start,
            step: this.props.step
          }
        },
        {
          condition: prevProps.tooltips !== this.props.tooltips,
          options: {
            range: customRange,
            start: this.props.start,
            tooltips: this.props.tooltips
          }
        },
        {
          condition: prevProps.helpMessage !== this.props.helpMessage || prevProps.errorMessage !== this.props.errorMessage || prevProps.successMessage !== this.props.successMessage || prevProps.employeeMessage !== this.props.employeeMessage,
          options: {
            ...!Array.isArray(this.props.start) ? { handleAttributes: this.getHandleAttributes(this.props.start, this.props.disabled) } : {},
            range: customRange,
            start: this.props.start
          },
          postUpdate: () => {
            if (!Array.isArray(this.props.start) && this.lowerHandle) {
              this.lowerHandle.setAttribute("aria-describedby", this.getAriaDescribedBy());
            } else if (Array.isArray(this.props.start) && this.sliderContainer) {
              this.sliderContainer.setAttribute("aria-describedby", this.getAriaDescribedBy());
            }
          }
        }
      ];
      optionsToUpdate.forEach((item) => {
        if (item.condition) {
          this.slider.updateOptions(item.options, false);
          if (item.postUpdate) {
            item.postUpdate();
          }
        }
      });
      this.props.disabled ? this.slider.disable() : this.slider.enable();
    }
    if (this.isSliderDisabled()) {
      this.removeListeners();
    }
  }
  componentDidMount() {
    this.addSlider();
    window.addEventListener("resize", this.debouncedResizeHandler);
  }
  componentWillUnmount() {
    this.removeSlider();
    window.removeEventListener("resize", this.debouncedResizeHandler);
    window.clearTimeout(this.tooltipStyleTimeout);
    window.clearInterval(this.tooltipStyleInterval);
    this.debouncedResizeHandler.clear();
  }
  BEM() {
    var _a, _b, _c;
    const classArray = ["slider"];
    const hasHelper = !!((_c = (_b = (_a = this.props.helpMessage) != null ? _a : this.props.errorMessage) != null ? _b : this.props.successMessage) != null ? _c : this.props.employeeMessage);
    if (this.props.hideLabel) {
      classArray.push("slider--no-label-" + this.props.hideLabel);
    }
    if (hasHelper) {
      classArray.push("slider--with-helper");
    }
    if (this.props.disabled) {
      classArray.push("is-disabled");
    }
    if (this.props.className) {
      classArray.push(this.props.className);
    }
    return classArray.join(" ");
  }
  getFilteredRange() {
    const { range, step = 1, pips } = this.props;
    const filteredRange = {
      min: range.min,
      max: range.max
    };
    const disabledValues = (pips == null ? void 0 : pips.filter((pip) => pip.disabled).map((pip) => pip.value)) || [];
    for (let value = range.min + step; value < range.max; value += step) {
      if (!disabledValues.includes(value)) {
        const percentage = (value - range.min) / (range.max - range.min) * 100;
        filteredRange["".concat(percentage, "%")] = value;
      }
    }
    return filteredRange;
  }
  getHandleAttributes(start, disabled) {
    return Array.isArray(start) ? [
      {
        "aria-label": this.props.labels.get("slider.from"),
        "aria-disabled": disabled ? "true" : "false"
      },
      {
        "aria-label": this.props.labels.get("slider.to"),
        "aria-disabled": disabled ? "true" : "false"
      }
    ] : [
      {
        "aria-labelledby": "".concat(this.props.id, "-label"),
        "aria-describedby": this.getAriaDescribedBy(),
        "aria-disabled": disabled ? "true" : "false"
      }
    ];
  }
  addSlider() {
    const cssPrefix = "slider__";
    const { range, start, step, tooltips, direction, disabled } = this.props;
    const format = this.setUnit(this.props.unit);
    const hasOnlyMinMax = Object.keys(range).length === 2 && "min" in range && "max" in range;
    const customRange = hasOnlyMinMax ? this.getFilteredRange() : range;
    const handleAttributes = this.getHandleAttributes(start, disabled);
    const sliderOptions = {
      connect: true,
      cssPrefix,
      direction,
      format,
      range: customRange,
      start,
      snap: true,
      step,
      tooltips,
      handleAttributes
    };
    if (this.sliderContainer) {
      this.slider = nouislider.create(this.sliderContainer, sliderOptions);
    }
    if (this.slider && this.sliderContainer) {
      this.slider.on("start", this.onStart.bind(this));
      this.slider.on("slide", this.onDrag);
      this.slider.on("update", this.onUpdate.bind(this));
      this.slider.on("change", this.changeValue.bind(this));
      this.slider.on("end", this.onEnd.bind(this));
      const handles = this.sliderContainer.querySelectorAll(".slider__handle");
      this.lowerHandle = handles[0];
      this.upperHandle = handles[1];
      this.sliderConnect = this.sliderContainer.querySelector(".slider__connect");
      const sliderTooltips = this.sliderContainer.querySelectorAll(".slider__tooltip");
      this.lowerTooltip = sliderTooltips[0];
      this.upperTooltip = sliderTooltips[1];
      this.origin = Array.from(this.sliderContainer.querySelectorAll(".slider__origin"));
      this.addListeners();
      this.handleChange();
    }
  }
  onStart(values) {
    if (Array.isArray(values) && this.sliderContainer) {
      this.sliderContainer.classList.add("slider__state-drag");
    }
  }
  onEnd() {
    if (this.sliderContainer) {
      this.sliderContainer.classList.remove("slider__state-drag");
    }
    window.clearTimeout(this.tooltipStyleTimeout);
    window.clearInterval(this.tooltipStyleInterval);
    this.tooltipStyleInterval = 0;
    this.handleChange();
  }
  addListeners() {
    if (this.sliderContainer) {
      this.sliderContainer.addEventListener("transitionend", this.handleTransitionEnd);
    }
  }
  removeListeners() {
    if (this.sliderContainer) {
      this.sliderContainer.removeEventListener("transitionend", this.handleTransitionEnd);
    }
  }
  removeSlider() {
    if (this.slider) {
      this.slider.destroy();
    }
    this.removeListeners();
  }
  isSliderDisabled() {
    const { disabled, pips } = this.props;
    if (disabled) {
      return disabled;
    }
    if (!pips) {
      return false;
    }
    return !pips.filter((pip) => !pip.disabled).length;
  }
  setCollapsedTooltipPosition() {
    if (this.lowerHandle && this.upperHandle && this.sliderContainer && this.collapsedTooltip) {
      const containerPadding = 12;
      const containerRect = this.sliderContainer.getBoundingClientRect();
      const containerLeft = containerRect.left - containerPadding;
      const containerRight = containerRect.right + containerPadding;
      const containerWidth = containerRect.width + containerPadding * 2;
      const lowerHandleLeft = this.lowerHandle.getBoundingClientRect().left;
      const upperHandleRight = this.upperHandle.getBoundingClientRect().right;
      const spaceBetween = upperHandleRight - lowerHandleLeft;
      const lowerHandleRelativeLeft = lowerHandleLeft - containerLeft;
      const leftPercent = (lowerHandleRelativeLeft + spaceBetween / 2) / containerWidth * 100;
      const tooltipWidth = this.collapsedTooltip.getBoundingClientRect().width;
      const handleMiddle = lowerHandleLeft + spaceBetween / 2;
      const tooltipRight = handleMiddle + tooltipWidth / 2;
      const tooltipLeft = handleMiddle - tooltipWidth / 2;
      const futureLeft = containerWidth * (leftPercent / 100) - tooltipWidth / 2 + containerLeft;
      if (tooltipRight >= containerRight || futureLeft + tooltipWidth >= containerRight) {
        this.setCollapsedTooltipPositionRight();
      } else if (tooltipLeft <= containerLeft || futureLeft <= containerLeft) {
        this.setCollapsedTooltipPositionLeft();
      } else {
        this.setCollapsedTooltipPositionCenter("".concat(leftPercent, "%"));
      }
    }
  }
  setCollapsedTooltipPositionRight() {
    const collapsedTooltipStyles = {
      left: "auto",
      right: "0px",
      transform: "none"
    };
    this.setState({
      collapsedTooltipStyles
    });
  }
  setCollapsedTooltipPositionLeft() {
    const collapsedTooltipStyles = {
      left: "0px",
      right: "auto",
      transform: "none"
    };
    this.setState({
      collapsedTooltipStyles
    });
  }
  setCollapsedTooltipPositionCenter(left) {
    const collapsedTooltipStyles = {
      left,
      right: "auto",
      transform: "translateX(-50%)"
    };
    this.setState({
      collapsedTooltipStyles
    });
  }
  collapseTooltips() {
    if (!this.state.tooltipsCollapsed) {
      this.setState(
        {
          tooltipsCollapsed: true
        },
        () => this.setCollapsedTooltipPosition()
      );
    }
  }
  unCollapseTooltips() {
    if (this.state.tooltipsCollapsed) {
      this.setState({
        collapsedTooltipStyles: {},
        tooltipsCollapsed: false
      });
    }
  }
  handleTooltip(handle, tooltip) {
    if (!this.sliderContainer || !handle || !tooltip) {
      return;
    }
    const containerPadding = 12;
    const tooltipLeftClass = "slider__tooltip--left";
    const tooltipRightClass = "slider__tooltip--right";
    const containerRect = this.sliderContainer.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const handleRect = handle.getBoundingClientRect();
    const containerLeft = containerRect.left - containerPadding;
    const containerRight = containerRect.right + containerPadding;
    const toolTipLeft = containerLeft - handleRect.left;
    if (tooltipRect.left < containerLeft && !tooltip.classList.contains(tooltipLeftClass)) {
      tooltip.classList.add(tooltipLeftClass);
      tooltip.setAttribute("style", "left: ".concat(toolTipLeft, "px"));
    } else if (tooltip.classList.contains(tooltipLeftClass)) {
      if (handleRect.left + handleRect.width / 2 - containerLeft > tooltipRect.width / 2) {
        tooltip.removeAttribute("style");
        tooltip.classList.remove(tooltipLeftClass);
      } else {
        tooltip.setAttribute("style", "left: ".concat(toolTipLeft, "px"));
      }
    }
    const toolTipRight = handleRect.right - containerRight;
    if (tooltipRect.right > containerRight && !tooltip.classList.contains(tooltipRightClass)) {
      tooltip.classList.add(tooltipRightClass);
      tooltip.setAttribute("style", "right: ".concat(toolTipRight, "px"));
    } else if (tooltip.classList.contains(tooltipRightClass)) {
      if (containerRight - (handleRect.right - handleRect.width / 2) > tooltipRect.width / 2) {
        tooltip.removeAttribute("style");
        tooltip.classList.remove(tooltipRightClass);
      } else {
        tooltip.setAttribute("style", "right: ".concat(toolTipRight, "px"));
      }
    }
  }
  onUpdate(values) {
    if (this.sliderContainer) {
      const sliderTooltips = this.sliderContainer.querySelectorAll(".slider__tooltip");
      this.lowerTooltip = sliderTooltips[0];
      this.upperTooltip = sliderTooltips[1];
    }
    const numberValue = this.toNumber(values);
    const start = Array.isArray(numberValue) && numberValue.length === 1 ? numberValue[0] : numberValue;
    if (this.isValueDisabled(start)) {
      this.changeToOldValue();
      return;
    }
    this.handleChange();
    this.setState({
      value: this.toNumber(values)
    });
  }
  changeValue(values) {
    const numberValue = this.toNumber(values);
    const start = Array.isArray(numberValue) && numberValue.length === 1 ? numberValue[0] : numberValue;
    if (this.isValueDisabled(start)) {
      this.changeToOldValue();
      return;
    }
    if (this.props.onChange) {
      this.props.onChange(start);
    }
  }
  isValueDisabled(values) {
    if (Array.isArray(values) && this.props.pips) {
      const valueOnePip = this.props.pips.find((pip) => pip.value === values[0]);
      const valueTwoPip = this.props.pips.find((pip) => pip.value === values[0]);
      return this.isPipDisabled(valueOnePip) || this.isPipDisabled(valueTwoPip);
    } else if (!Array.isArray(values) && this.props.pips) {
      const valuePip = this.props.pips.find((pip) => pip.value === values);
      return this.isPipDisabled(valuePip);
    }
    return false;
  }
  isPipDisabled(pip) {
    return pip && pip.disabled || false;
  }
  getPrecision(a) {
    if (!isFinite(a)) {
      return 0;
    }
    let e = 1;
    let p = 0;
    while (Math.round(a * e) / e !== a) {
      e *= 10;
      p++;
    }
    return p;
  }
  setUnit(unit = "") {
    return {
      from: (value) => Number(unit ? value.replace(" " + unit, "") : value),
      to: (value) => unit ? this.getFixedNumber(value) + " " + unit : this.getFixedNumber(value)
    };
  }
  getFixedNumber(value) {
    return this.props.step ? value.toFixed(this.getPrecision(this.props.step)) : value.toFixed();
  }
  toNumber(values) {
    return Array.isArray(values) ? values.map((value) => parseFloat(String(value))) : parseFloat(String(values));
  }
  onPipClick(value) {
    if (this.props.onChange) {
      this.props.onChange(value);
    }
    if (this.slider) {
      this.slider.set(value);
    }
  }
  getPipStyle(value, position) {
    const { range, direction, pips } = this.props;
    if (!pips) {
      return;
    }
    const pipMaxWidth = 100 / pips.length;
    if (value === range.min && direction !== "rtl" || value === range.max && direction === "rtl") {
      return {
        left: "-12px",
        maxWidth: "".concat(pipMaxWidth, "%"),
        transform: "none"
      };
    } else if (value === range.max && direction !== "rtl" || value === range.min && direction === "rtl") {
      return {
        maxWidth: "".concat(pipMaxWidth, "%"),
        right: "-12px",
        textAlign: "right",
        transform: "none"
      };
    }
    return {
      left: "".concat(position, "%"),
      maxWidth: "".concat(pipMaxWidth, "%")
    };
  }
  pipBEM(pip) {
    const classArray = ["slider__pip"];
    const { value } = this.state;
    if (pip.value === value || Array.isArray(value) && value.includes(pip.value)) {
      classArray.push("slider__pip--active");
    }
    if (pip.disabled) {
      classArray.push("slider__pip--disabled");
    }
    return classArray.join(" ");
  }
  renderPips(pips) {
    return /* @__PURE__ */ React.createElement("div", { className: "slider__pips-wrapper" }, pips.map(this.renderPip));
  }
  getCollapsedTooltip() {
    if (!this.slider) {
      return null;
    }
    let parsedValue = "";
    const value = this.toNumber(this.slider.get());
    if (Array.isArray(value)) {
      parsedValue = value[0] !== value[1] ? "".concat(value[0], " - ").concat(value[1]) : value[0];
    } else {
      parsedValue = value;
    }
    return /* @__PURE__ */ React.createElement(
      "div",
      {
        className: "slider__tooltip slider__tooltip--collapsed",
        style: this.state.collapsedTooltipStyles,
        ref: (tooltip) => this.collapsedTooltip = tooltip
      },
      parsedValue,
      " ",
      this.props.unit
    );
  }
  getAriaDescribedBy() {
    return clsx(
      this.props.helpMessage && "".concat(this.props.id, "-help-message"),
      this.props.errorMessage && "".concat(this.props.id, "-error-message"),
      this.props.successMessage && "".concat(this.props.id, "-success-message"),
      this.props.employeeMessage && "".concat(this.props.id, "-employee-message")
    );
  }
  renderFormHelper() {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, this.props.helpMessage && /* @__PURE__ */ React.createElement(FormHelper, { text: this.props.helpMessage, type: "help", className: "slider__form-helper", id: "".concat(this.props.id, "-help-message") }), this.props.errorMessage && /* @__PURE__ */ React.createElement(FormHelper, { text: this.props.errorMessage, type: "error", className: "slider__form-helper", id: "".concat(this.props.id, "-error-message") }), this.props.successMessage && /* @__PURE__ */ React.createElement(FormHelper, { text: this.props.successMessage, type: "valid", className: "slider__form-helper", id: "".concat(this.props.id, "-success-message") }), this.props.employeeMessage && /* @__PURE__ */ React.createElement(FormHelper, { text: this.props.employeeMessage, type: "employee", className: "slider__form-helper", id: "".concat(this.props.id, "-employee-message") }));
  }
  render() {
    const { labelSize = "h6", label, pips, hidePips, start, id } = this.props;
    const isRange = Array.isArray(start);
    const sliderContent = /* @__PURE__ */ React.createElement("div", { className: "slider__inner", ref: (slider) => this.sliderContainer = slider });
    const Element2 = isRange ? "fieldset" : "div";
    const Label = isRange ? "legend" : "label";
    return /* @__PURE__ */ React.createElement(Element2, { className: this.BEM(), id, "aria-describedby": isRange ? this.getAriaDescribedBy() : void 0 }, /* @__PURE__ */ React.createElement(Label, { className: clsx("slider__label", labelSize), id: "".concat(id, "-label") }, label), this.renderFormHelper(), sliderContent, pips && !hidePips && this.renderPips(pips), this.state.tooltipsCollapsed && this.getCollapsedTooltip());
  }
}
export {
  Slider,
  SliderInner
};
